import { get, isEmpty } from "lodash";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import "../../eq-icons.css";
import { getLinearGradientFromGradientArray } from "../../shared/utils/PdfTraining";

function FirstTable({ training, slowTraining }) {
  const [chrono, setChrono] = useState([]);
  const [hr, setHr] = useState([]);
  const [shoeing, setShoeing] = useState([]);
  const [insightItems, setInsightItems] = useState([]);
  var asterisksCount = slowTraining ? 0 : 3;

  useEffect(() => {
    let insightItems = get(training, "insight_items", []);

    if (!isEmpty(insightItems)) {
      setInsightItems(insightItems);
      setChrono(
        [...insightItems].filter((e) =>
          ["speed", "sectionals", "averages", "splitTimes", "general"].includes(
            get(e, "insight_parameter.category"),
          ),
        ),
      );
      setHr(
        [...insightItems].filter((e) =>
          ["heartRate", "recovery"].includes(
            get(e, "insight_parameter.category"),
          ),
        ),
      );
      setShoeing(
        [...insightItems].filter((e) =>
          ["locomotion"].includes(get(e, "insight_parameter.category")),
        ),
      );
    }
  }, [training]);

  function getParameterName(row) {
    var name = get(
      row,
      "insight_parameter.short_name_translated",
      get(row, "insight_parameter.name_translated"),
    );
    var insightParameter = row.insight_parameter;

    if (insightParameter.name == "insight.max_speed") {
      name += "  " + "*".repeat(asterisksCount);
      asterisksCount++;
    }

    if (insightParameter.name == "insight.stride_frequency_at_60") {
      name += "  " + "*".repeat(asterisksCount);
      asterisksCount++;
    }

    return name ?? "-";
  }

  const sections = [
    {
      icon: "/icons8-timer-64.png",
      rows: chrono,
    },
    {
      icon: "/icons8-heart-rate-80.png",
      rows: hr,
    },
    {
      icon: "/horseshoe.png",
      rows: shoeing,
    },
  ];

  // Meta
  const insightLength = insightItems.length;
  const itemsPerRow =
    insightLength % 3 === 0 ? 3 : insightLength % 2 === 0 ? 2 : 3;

  return (
    <Box
      sx={{
        border: "2px solid",
        borderColor: "#4a0e0d",
        borderRadius: "6px",
        overflow: "hidden",
        mx: "auto",
      }}
    >
      {!slowTraining ? (
        sections.map((section, index) => (
          <Table
            key={index}
            sx={{
              borderCollapse: "collapse",
              "& tr:nth-of-type(odd)": {
                backgroundColor: "#eee",
              },
              width: "100%",
              borderBottom:
                index !== sections.length - 1 ? "1px solid #4a0e0d" : "none",
            }}
          >
            <TableBody>
              {section.rows.map((row, i) => (
                <TableRow key={i} sx={{ overflow: "hidden" }}>
                  {i === 0 && (
                    <TableCell
                      rowSpan={section.rows.length}
                      className="bg-gradient"
                      sx={{
                        width: "5%",
                        textAlign: "center",
                        position: "relative",
                        p: 0,
                        border: "none",
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          overflow: "hidden",
                          zIndex: 20,
                          margin: "auto",
                          backgroundImage: `url(${section.icon})`,
                          backgroundSize: "contain",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 400,
                      width: "17.5%",
                      textAlign: "center",
                      p: 1,
                      border: "none",
                    }}
                  >
                    {getParameterName(row)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      width: "19%",
                      textAlign: "center",
                      px: 0,
                      py: 1,
                      border: "none",
                    }}
                  >
                    {get(row, "formatted_value", "-")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      textAlign: "center",
                      width: "58.5%",
                      p: 1,
                      border: "none",
                    }}
                  >
                    {row.hasOwnProperty("text") ? (
                      row?.text
                    ) : (
                      <Slider item={row} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ))
      ) : (
        <Box>
          {/* Body Grid: 3 or 2 Vertical Columns */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${itemsPerRow}, minmax(0, 1fr))`,
            }}
          >
            {insightItems.map((row, rIdx) => (
              <Box
                key={rIdx}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  borderBottom:
                    rIdx > insightLength - itemsPerRow - 1
                      ? "none"
                      : "1px solid rgba(74, 14, 13, 0.2)",
                  borderRight:
                    (rIdx + 1) % itemsPerRow !== 0
                      ? "1px solid rgba(74, 14, 13, 0.2)"
                      : "none",
                  "&:last-child": {
                    borderRight: "none",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "10px",
                    fontWeight: 500,
                    flex: 1,
                    overflow: "hidden",
                    color: "grey.700",
                    lineHeight: 1.25,
                    position: "relative",
                    borderRight: "0.5px solid rgba(0, 0, 0, 0.12)",
                    gap: 1,
                  }}
                >
                  <Box
                    className="bg-gradient"
                    sx={{
                      fontSize: "9px",
                      width: "50%",
                      flexShrink: 0,
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      component="span"
                      className={`eq-${row?.insight_parameter?.icon} eq-icon-2`}
                      sx={{ color: "#fff", fontWeight: 600 }}
                    >
                      <span className="path1"></span>
                      <span className="path2"></span>
                      <span className="path3"></span>
                      <span className="path4"></span>
                      <span className="path5"></span>
                      <span className="path6"></span>
                      <span className="path7"></span>
                      <span className="path8"></span>
                    </Box>
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      color: "grey.900",
                      fontSize: "9.5px",
                      mx: "auto",
                      textAlign: "center",
                      fontWeight: 600,
                    }}
                  >
                    {getParameterName(row)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 0.75,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "9.5px",
                      fontWeight: 600,
                      color: "grey.900",
                      mb: 0.5,
                    }}
                  >
                    {get(row, "formatted_value", "-")}
                  </Typography>
                </Box>
              </Box>
            ))}

            {insightItems.length === 0 && (
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                  fontSize: "10px",
                  color: "grey.400",
                  fontStyle: "italic",
                }}
              >
                No data available
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function Slider({ item }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
      <Typography
        component="p"
        sx={{
          fontSize: "10px",
          fontWeight: 400,
          width: 64,
          textAlign: "right",
          mr: 0.5,
        }}
      >
        {get(item, "min")}
      </Typography>
      <StyledDiv gradients={get(item, "gradient_colors", [])}>
        <Box
          sx={{
            width: "14px",
            height: "14px",
            border: "2px solid #e5e7eb !important",
            top: "-3px",
            borderRadius: "50%",
            position: "absolute",
            zIndex: 10,
            transform: "translate3d(-50%, 0, 0)",
            WebkitTransform: "translate3d(-50%, 0, 0)",
            willChange: "transform",
            left: `${get(item, "value", 0)}%`,
            backgroundColor: get(item, "cursor_color", "lightblue"),
          }}
        />

        {!isEmpty(item.sections) &&
          item.sections.map((s, idx) => (
            <Typography
              key={idx}
              component="p"
              sx={{
                fontSize: "9px",
                fontWeight: 400,
                position: "absolute",
                transform: "translateX(-50%)",
                left: `${s.position}%`,
                bottom: "-18px",
              }}
            >
              {s.value}
            </Typography>
          ))}
      </StyledDiv>
      <Typography
        component="p"
        sx={{
          fontSize: "10px",
          fontWeight: 400,
          width: 64,
          textAlign: "left",
          ml: 0.5,
        }}
      >
        {get(item, "max")}
      </Typography>
    </Box>
  );
}

function StyledDiv({ gradients, children }) {
  let [str, setStr] = useState("");

  useEffect(() => {
    if (!isEmpty(gradients)) {
      setStr(getLinearGradientFromGradientArray(gradients));
    }
  }, [gradients]);

  if (isEmpty(gradients)) return <></>;
  return (
    <Box
      sx={{
        width: "100%",
        height: 8,
        borderRadius: "8px",
        position: "relative",
        p: 0,
        m: 0,
        boxSizing: "border-box",
        flex: 1,
        background: str,
      }}
    >
      {children}
    </Box>
  );
}

export default FirstTable;
